<template>
  <content-container class="!p-4" :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-select
          v-model="params.companyId"
          filterable
          placeholder="企业名称"
          clearable
        >
          <el-option
            v-for="item in optionsCompany"
            :key="item.id"
            :label="item.companyName"
            :value="item.id"
          />
        </el-select>
      </search-col>

      <search-col>
        <el-date-picker
          v-model="dateRange"
          style="width: 100%"
          type="daterange"
          :disabled-date="disabledDate"
          start-placeholder="结算周期(起)"
          :default-time="defaultTimeFormat"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          end-placeholder="结算周期(止)"
          @keydown.enter="doSearch"
        />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>
   
    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" :cell-style="initOperatorColumnStyle">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="settlementDate" label="结算周期" :min-width="220" />
      <el-table-column align="left" prop="companyName" label="企业名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="left" prop="agentTopName" label="特级代理商名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="left" prop="serverName" label="服务商名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="left" prop="orderNo" label="订单编号" :min-width="160" />
      <el-table-column align="right" prop="successSettleAmount" label="成功结算金额(元)" :min-width="160" />
      <el-table-column align="right" prop="serviceRate" label="发生服务费率(%)" :min-width="160" />
      <el-table-column align="right" prop="serviceAmount" label="发生服务费金额(元)" :min-width="160" />
      <el-table-column v-if="!isAgent" align="right" prop="serverRate" label="平台成本(%)" :min-width="160" />
      <el-table-column align="right" prop="agentTopRate" label="特代成本(%)" :min-width="160" />
      <el-table-column align="right" prop="agentOneRate" label="一代成本(%)" :min-width="160" />
      <el-table-column align="right" prop="agentTwoRate" label="二代成本(%)" :min-width="160" />
      <el-table-column align="right" prop="companyRate" label="企业成本(%)" :min-width="160" />
      <el-table-column align="right" prop="remainAmount" label="留存成本(元)" :min-width="160" />
      <el-table-column v-if="!isAgent" align="right" prop="serverAmount" label="服务商收益(元)" :min-width="160" />
      <el-table-column v-if="!isAgent" align="right" prop="techAmount" label="平台收益(元)" :min-width="160" />
      <el-table-column align="right" prop="agentDivideAmount" label="代理商可分总额(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTopAvailableAmount" label="特代可得(元)" :min-width="160" />
      <el-table-column align="right" prop="agentOneAvailableAmount" label="一代可得(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTwoAvailableAmount" label="二代可得(元)" :min-width="160" />
      <el-table-column align="right" prop="protectLineRate" label="推广费率(%)" :min-width="160" />
      <el-table-column align="right" prop="protectLineAmount" label="保线金额(元)" :min-width="160" />
      <el-table-column align="right" prop="outLineAmount" label="推广服务费合计(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTopOutLineAmount" label="特代推广服务费(元)" :min-width="160" />
      <el-table-column align="right" prop="agentOneOutLineAmount" label="一代推广服务费(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTwoOutLineAmount" label="二代推广服务费(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTopInLineAmount" label="特代溢价服务费(元)" :min-width="160" />
      <el-table-column align="right" prop="agentOneInLineAmount" label="一代溢价服务费(元)" :min-width="160" />
      <el-table-column align="right" prop="agentTwoInLineAmount" label="二代溢价服务费(元)" :min-width="160" />

      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  useUserStore,

} from '@core/main';
import { IDistributionTaskCompanyDetail } from '@core/main/types';
import { getFomattedTimeRange } from '@core/tools';
import { getAllCompanyListForReport, queryDistributionTaskCompanyPage } from '@core/main/api';
import { ESystemType } from '@core/api';
import { useRoute } from 'vue-router';

const route = useRoute();

defineOptions({ name: 'ReportCenterAgent' });

const { state: userState } = useUserStore();

const isAgent = computed(() => userState.identity.systemType === ESystemType.Agent);

const defaultPageSize = 12;

const params = reactive({
  page: 1,
  size: defaultPageSize,
  taskId: Number(route.query.id) || Number(route.query.refId) || undefined,
  companyId: Number(route.query.companyId) || undefined,
});

const loading = ref(false);
const total = ref(0);

const tableData = ref<IDistributionTaskCompanyDetail[]>([]);

const optionsCompany = ref<any>([]);
const dateRange = ref<any>(route.query.settlementDateStart ? [route.query.settlementDateStart, route.query.settlementDateEnd] : []);
const defaultTimeFormat = computed (() => getFomattedTimeRange());
const disabledDate = (current: Date) => current.getTime() > Date.now();

const { buttons } = useTblPermissionButtons([]);

async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryDistributionTaskCompanyPage({
      ...params,
      settlementDateStart: dateRange.value ? dateRange.value[0] : undefined,
      settlementDateEnd: dateRange.value ? dateRange.value[1] : undefined,
    });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  dateRange.value = [];
  for (const key in params) {
    if (!['page', 'size', 'taskId'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

async function getCompanyList() {
  try {

    const { data } = await getAllCompanyListForReport();

    optionsCompany.value = data;

  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

function init() {
  getCompanyList();
}

init();
fetchData();
</script>
